/**
 * Configuration to hold stage-specific constants for ADDAPT
 */

export interface StageConfig {
  API_GATEWAY_ENDPOINTS: {
    endpoints: Array<{
      name: string;
      endpoint: string;
    }>;
  };
  WEBSOCKET_ENDPOINTS: {
    default: string;
    ai: string;
  };
  AI_HTTP_ENDPOINT: string;
  S3_BUCKET: string;
  CLOUDFRONT_URL?: string;
  ENABLE_MOCK_DATA?: boolean;
}

const AppConfig: Record<string, StageConfig> = {
  local: {
    API_GATEWAY_ENDPOINTS: {
      endpoints: [
        {
          name: "ADDAPT_SERVICE_API",
          endpoint: "https://fbvxdonh58.execute-api.us-east-1.amazonaws.com/beta" // Using beta endpoint for local dev
        }
      ]
    },
    WEBSOCKET_ENDPOINTS: {
      default: "wss://y0wea9uz3m.execute-api.us-east-1.amazonaws.com/beta",
      ai: "wss://y0wea9uz3m.execute-api.us-east-1.amazonaws.com/beta"
    },
    AI_HTTP_ENDPOINT: "https://s6d6752vnc.execute-api.us-east-1.amazonaws.com/beta",
    S3_BUCKET: "addapt-documents-local",
    ENABLE_MOCK_DATA: true
  },
  beta: {
    API_GATEWAY_ENDPOINTS: {
      endpoints: [
        {
          name: "ADDAPT_SERVICE_API",
          endpoint: "https://fbvxdonh58.execute-api.us-east-1.amazonaws.com/beta"
        }
      ]
    },
    WEBSOCKET_ENDPOINTS: {
      default: "wss://y0wea9uz3m.execute-api.us-east-1.amazonaws.com/beta",
      ai: "wss://y0wea9uz3m.execute-api.us-east-1.amazonaws.com/beta"
    },
    AI_HTTP_ENDPOINT: "https://s6d6752vnc.execute-api.us-east-1.amazonaws.com/beta",
    S3_BUCKET: "addapt-documents-beta",
    CLOUDFRONT_URL: "https://d1234567890.cloudfront.net",
    ENABLE_MOCK_DATA: false
  },
  gamma: {
    API_GATEWAY_ENDPOINTS: {
      endpoints: [
        {
          name: "ADDAPT_SERVICE_API",
          endpoint: "https://fbvxdonh58.execute-api.us-east-1.amazonaws.com/gamma"
        }
      ]
    },
    WEBSOCKET_ENDPOINTS: {
      default: "wss://y0wea9uz3m.execute-api.us-east-1.amazonaws.com/gamma",
      ai: "wss://y0wea9uz3m.execute-api.us-east-1.amazonaws.com/gamma"
    },
    AI_HTTP_ENDPOINT: "https://s6d6752vnc.execute-api.us-east-1.amazonaws.com/gamma",
    S3_BUCKET: "addapt-documents-gamma",
    CLOUDFRONT_URL: "https://d2345678901.cloudfront.net",
    ENABLE_MOCK_DATA: false
  },
  production: {
    API_GATEWAY_ENDPOINTS: {
      endpoints: [
        {
          name: "ADDAPT_SERVICE_API",
          endpoint: "https://fbvxdonh58.execute-api.us-east-1.amazonaws.com/prod"
        }
      ]
    },
    WEBSOCKET_ENDPOINTS: {
      default: "wss://y0wea9uz3m.execute-api.us-east-1.amazonaws.com/prod",
      ai: "wss://y0wea9uz3m.execute-api.us-east-1.amazonaws.com/prod"
    },
    AI_HTTP_ENDPOINT: "https://s6d6752vnc.execute-api.us-east-1.amazonaws.com/prod",
    S3_BUCKET: "addapt-documents-prod",
    CLOUDFRONT_URL: "https://d3456789012.cloudfront.net",
    ENABLE_MOCK_DATA: false
  }
};

/**
 * Get configuration for a specific stage
 * @param stage - The deployment stage (local, beta, gamma, production)
 * @returns Stage-specific configuration
 */
export const getStageConfig = (stage: string): StageConfig => {
  return AppConfig[stage] || AppConfig.production;
};

/**
 * Get API endpoint URL for a specific stage
 * @param stage - The deployment stage
 * @param apiName - The API name (default: ADDAPT_SERVICE_API)
 * @returns API endpoint URL
 */
export const getApiEndpoint = (stage: string, apiName: string = "ADDAPT_SERVICE_API"): string => {
  const config = getStageConfig(stage);
  const endpoint = config.API_GATEWAY_ENDPOINTS.endpoints.find(e => e.name === apiName);
  return endpoint?.endpoint || "";
};

/**
 * Get WebSocket endpoint URL for a specific stage
 * @param stage - The deployment stage
 * @param useAI - Whether to use the AI WebSocket endpoint
 * @returns WebSocket endpoint URL
 */
export const getWebSocketEndpoint = (stage: string, useAI: boolean = false): string => {
  const config = getStageConfig(stage);
  return useAI ? config.WEBSOCKET_ENDPOINTS.ai : config.WEBSOCKET_ENDPOINTS.default;
};

/**
 * Get AI HTTP endpoint URL for a specific stage
 * @param stage - The deployment stage
 * @returns AI HTTP endpoint URL
 */
export const getAIHttpEndpoint = (stage: string): string => {
  const config = getStageConfig(stage);
  return config.AI_HTTP_ENDPOINT;
};

/**
 * Check if mock data should be used for a specific stage
 * @param stage - The deployment stage
 * @returns Whether to use mock data
 */
export const shouldUseMockData = (stage: string): boolean => {
  const config = getStageConfig(stage);
  return config.ENABLE_MOCK_DATA || false;
};

export default AppConfig;